import { useState } from "react";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { Leaf, ArrowRight, Sparkles } from "lucide-react";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { NOTCH } from "@/constants/testIds";

export default function AuthPage() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [mode, setMode] = useState("login"); // 'login' | 'signup'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (user && user !== false) return <Navigate to={loc.state?.from || "/"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
      navigate(loc.state?.from || "/", { replace: true });
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <div className="h-9 w-9 rounded-full bg-[#2C4C3B] flex items-center justify-center">
            <Leaf size={18} className="text-[#F9F8F6]" />
          </div>
          <span className="font-serif-display text-3xl text-[#2C4C3B]">Notch</span>
        </div>

        <div className="bg-white rounded-[1.75rem] border border-[#E5E2DC] p-7 sm:p-9 shadow-[0_20px_60px_-30px_rgba(44,76,59,0.25)]">
          <h1 className="font-serif-display text-3xl text-[#2C4C3B] leading-tight mb-2">
            {mode === "login" ? "Welcome back." : "Start your bowl diary."}
          </h1>
          <p className="text-sm text-[#666] mb-7">
            {mode === "login" ? "Pick up where you left off." : "6 free meal plans on us. ₹49/month after that."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "signup" && (
              <Field label="Your name">
                <input
                  data-testid={NOTCH.authName}
                  value={name} onChange={(e) => setName(e.target.value)}
                  required
                  className="auth-input"
                  placeholder="What should we call you?"
                />
              </Field>
            )}
            <Field label="Email">
              <input
                data-testid={NOTCH.authEmail}
                type="email" autoComplete="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="auth-input"
                placeholder="you@example.com"
              />
            </Field>
            <Field label="Password">
              <input
                data-testid={NOTCH.authPassword}
                type="password" autoComplete={mode === "login" ? "current-password" : "new-password"} required
                minLength={6}
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="auth-input"
                placeholder="At least 6 characters"
              />
            </Field>

            {err && (
              <div data-testid={NOTCH.authError} className="rounded-2xl bg-[#FBEDE7] border border-[#E9C2B3] text-[#7a3320] px-4 py-2.5 text-sm">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid={NOTCH.authSubmit}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#2C4C3B] text-white text-base font-medium hover:bg-[#1E3629] transition-all duration-300 focus:ring-4 focus:ring-[#2C4C3B]/20 disabled:opacity-60"
            >
              {busy ? "…" : (<>{mode === "login" ? "Sign in" : "Create account"}<ArrowRight size={16} /></>)}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-[#E5E2DC] text-center text-sm text-[#666]">
            {mode === "login" ? "New here?" : "Already have an account?"}{" "}
            <button
              type="button"
              data-testid={NOTCH.authToggle}
              onClick={() => { setMode(mode === "login" ? "signup" : "login"); setErr(""); }}
              className="text-[#2C4C3B] font-medium hover:underline"
            >
              {mode === "login" ? "Create one" : "Sign in"}
            </button>
          </div>
        </div>

        <p className="mt-6 text-xs text-[#888] text-center inline-flex items-center justify-center w-full gap-1">
          <Sparkles size={12} /> 6 free plans · ₹49/month for unlimited
        </p>
      </div>
      <style>{`.auth-input{width:100%;border-radius:9999px;border:1px solid #D1CDD2;background:#fff;padding:.85rem 1.25rem;font-size:.95rem;color:#222;outline:none;transition:all .2s}.auth-input::placeholder{color:#aaa}.auth-input:focus{border-color:#2C4C3B;box-shadow:0 0 0 4px rgba(44,76,59,.12)}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.16em] text-[#888] mb-1.5">{label}</span>
      {children}
    </label>
  );
}
